/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../../utils/axios'; 
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { namesContainerStyles } from './styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  registerUser,
  registerWithGoogle,
} from '../../services/post/register/register';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { MuiTelInput } from 'mui-tel-input';
import { useAuth } from '../../../../context/AuthContext/AuthContext';
import { trackSignUp } from '../../../../utils/gtm/GTMService';
import { useAccessControl } from '../../../../context/AccessControlContext/AccessControlContext';
import { useGoogleLogin } from '@react-oauth/google';
import PasswordStrengthIndicator, {
  passwordRegex,
} from '../../components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import ThirdPartyAuthButtons from '../../components/ThirdPartyAuthButtons/ThirdPartyAuthButtons';
import OrDivider from '../../components/OrDivider/OrDivider';
import { inputBorderSx } from '../../styles/commonInputStyles';
import { registerColors } from '../../styles/colors';
import { getMaxPhoneDigits, getMinPhoneDigits } from './phoneLength';

interface CountryItem {
  countryCode: string;
  commonName: string;
}

const RegisterContent = () => {
  const theme = useTheme();
  const { t } = useTranslation('register');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    countryName,
  } = useAccessControl();
  const { user, login, loginWithTokens } = useAuth();

  const [countriesList, setCountriesList] = useState<CountryItem[]>([]);
  const { getCode } = require('country-list');
  const [form, setForm] = useState({
    type: 'default',
    firstname: '',
    lastname: '',
    email: '',
    phoneNumber: '',
    password: '',
    country: '',
    city: '',
    street: '',
    zipCode: '',
    referredBy: localStorage.getItem('affiliate') || Cookies.get('affiliate'),
  });

  useEffect(() => {
    const affiliateParam = searchParams.get('affiliate');
    if (affiliateParam) {
      localStorage.setItem('affiliate', affiliateParam);
      Cookies.set('affiliate', affiliateParam, { expires: 30 });
      setForm(prev => ({ ...prev, referredBy: affiliateParam }));
    }
  }, [searchParams]);

  const [defaultCountry, setDefaultCountry] = useState('');

  const [fieldErrors, setFieldErrors] = useState<{
    firstname?: string;
    lastname?: string;
    email?: string;
    phoneNumber?: string;
    password?: string;
    city?: string;
    street?: string;
    zipCode?: string;
    country?: string;
    acceptTnc?: string;
    newsletter?: string;
  }>({});

  const [acceptTnc, setAcceptTnc] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [google409Error, setGoogle409Error] = useState<string | null>(null);

  useEffect(() => {
    if (countryName && !form.country) {
      setForm((prev) => ({ ...prev, country: countryName }));
    }
  }, [countryName, form.country]);

  useEffect(() => {
    if (user) {
      if (user.role === 'ROLE_ADMIN') {
        navigate('/admin/dashboard');
      } else {
        navigate('/buy-funded');
      }
    }
  }, [user, navigate]);

useEffect(() => {
  axiosInstance
    .get('/compliance/countries/allowed')
    .then((response) => {
      setCountriesList(response.data.data || []);
    })
    .catch((err) => console.error('Failed to fetch countries:', err));
}, []);

useEffect(() => {
  axiosInstance
    .get('/compliance/countries/detect')
    .then((response) => {
      const country = response.data?.data?.commonName || '';
      setDefaultCountry(country);
    })
    .catch((err) => console.error('Failed to fetch default country:', err));
}, []);

  useEffect(() => {
    if (defaultCountry && countriesList.length > 0) {
      const foundItem = countriesList.find(
        (item) => item.commonName.toLowerCase() === defaultCountry.toLowerCase(),
      );
      if (foundItem) {
        setForm((prev) => ({ ...prev, country: foundItem.commonName }));
      }
    }
  }, [defaultCountry, countriesList]);

  const handlePhoneChange = (newValue: string, info: { countryCallingCode: string | null; countryCode: string | null }) => {
    if (!info.countryCallingCode) {
      setForm((prev) => ({ ...prev, phoneNumber: newValue }));
      setPhoneIsValid(false);
      return;
    }
    const dialCode = `+${info.countryCallingCode}`;
    // Prevent deleting the country dial code — keep at least the prefix
    if (!newValue.startsWith(dialCode)) {
      setForm((prev) => ({ ...prev, phoneNumber: `${dialCode} ` }));
      setPhoneIsValid(false);
      return;
    }

    const digitsOnly = newValue.replace(/\D/g, '');
    const maxDigits = getMaxPhoneDigits(info.countryCode, info.countryCallingCode);
    // Block input if exceeding the country-specific max length
    if (digitsOnly.length > maxDigits) return;

    const minDigits = getMinPhoneDigits(info.countryCode, info.countryCallingCode);
    const isValid = digitsOnly.length >= minDigits && digitsOnly.length <= maxDigits;

    setForm((prev) => ({ ...prev, phoneNumber: newValue }));
    setFieldErrors((prev) => ({ ...prev, phoneNumber: '' }));
    setPhoneIsValid(isValid);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>,
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateFields = (): boolean => {
    const newErrors: typeof fieldErrors = {};

    if (!form.firstname.trim()) newErrors.firstname = t('THIS_FIELD_IS_REQUIRED');
    if (!form.lastname.trim()) newErrors.lastname = t('THIS_FIELD_IS_REQUIRED');
    if (!form.email.trim()) newErrors.email = t('THIS_FIELD_IS_REQUIRED');

    if (!form.phoneNumber.trim()) {
      newErrors.phoneNumber = t('THIS_FIELD_IS_REQUIRED');
    } else if (!phoneIsValid) {
      newErrors.phoneNumber = t('INVALID_PHONE_FORMAT');
    }

    if (!form.password.trim()) {
      newErrors.password = t('THIS_FIELD_IS_REQUIRED');
    } else if (!passwordRegex.test(form.password)) {
      newErrors.password = t('PASSWORD_DO_NOT_MATCH_CRITERIA');
    }

    if (!form.country.trim()) newErrors.country = t('THIS_FIELD_IS_REQUIRED');
    if (!form.city.trim()) newErrors.city = t('THIS_FIELD_IS_REQUIRED');
    if (!form.street.trim()) newErrors.street = t('THIS_FIELD_IS_REQUIRED');
    if (!form.zipCode.trim()) newErrors.zipCode = t('THIS_FIELD_IS_REQUIRED');

    if (!acceptTnc) newErrors.acceptTnc = t('YOU_MUST_ACCEPT_TERMS_AND_CONDITIONS');
    if (!newsletter) newErrors.newsletter = t('YOU_MUST_SUBSCRIBE_TO_THE_NEWSLETTERS');

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length > 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const hasErrors = validateFields();
    if (hasErrors) {
      setIsSubmitting(false);
      return;
    }

    try {
      await registerUser(form);
      trackSignUp();
      await login(form.email, form.password, true);

      setTimeout(() => navigate('/buy-funded'), 200);
    } catch (err: any) {
      if (err.status === 409) {
        setFieldErrors((prev) => ({
          ...prev,
          email: t('ERROR_EMAIL_ALREADY_USED', { email: form.email }),
        }));
      } else if (err.fieldErrors && typeof err.fieldErrors === 'object') {
        setFieldErrors((prev) => ({ ...prev, ...(err.fieldErrors as typeof fieldErrors) }));
      } else {
        setError(err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleRegister = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const referredBy = localStorage.getItem('affiliate') || Cookies.get('affiliate');
        const res = await registerWithGoogle(tokenResponse.access_token, referredBy || undefined);
               await loginWithTokens(
        res.data.accessToken,
        res.data.refreshToken,
        'google',
        );
      } catch (error) {
        setGoogle409Error(t('ERROR_THIRD_PARTY_REGISTRATION_FAILED'));
      }
    },
    onError: () => {
      setGoogle409Error(t('ERROR_THIRD_PARTY_REGISTRATION_FAILED'));
    },
  });

  const handleRfxRegister = () => {
    const rfxAuthUrl = process.env.REACT_APP_RFX_AUTH_URL?.trim();

    window.open(`${rfxAuthUrl}/auth-popup.html`, 'rfxAuth', 'width=500,height=700');

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== rfxAuthUrl) return;

      const { data } = event;

      if (data.status === 'SUCCESS') {
        const params = new URLSearchParams();
        params.set('token', data.temporaryToken);
        if (data.emailOnRmf) params.set('emailOnRmf', data.emailOnRmf);
        if (data.createRmfAccount) params.set('createRmfAccount', 'true');
        if (data.isFirstSync) params.set('isFirstSync', 'true');
        if (data.lang) params.set('lang', data.lang);

        window.location.href = `/sso?${params.toString()}`;
      }

      if (data.status === 'ERROR') {
        setError(data.message || t('ERROR_THIRD_PARTY_REGISTRATION_FAILED'));
      }

      window.removeEventListener('message', handleMessage);
    };

    window.addEventListener('message', handleMessage);
  };

  return (
    <Stack width="100%">
      <Stack mt={-4} mb={4} width="100%">
        {google409Error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {google409Error}
          </Alert>
        )}
      </Stack>

      <ThirdPartyAuthButtons
        onGoogleClick={() => handleGoogleRegister()}
        onRaiseFxClick={handleRfxRegister}
        googleButtonText={t('BUTTON_SIGN_UP_WITH_GOOGLE')}
        raiseFxButtonText={t('BUTTON_SIGN_UP_WITH_RAISEFX')}
      />

      <OrDivider text={t('DIVIDER_OR')} />

      <form onSubmit={handleSubmit}>

        {/* Nom & prénom */}
        <Stack css={namesContainerStyles} gap={1.5} mb={1.5}>
          <TextField
            required
            id="firstname"
            label={t('LABEL_FIRSTNAME')}
            variant="outlined"
            placeholder={t('PLACEHOLDER_FIRSTNAME')}
            type="text"
            fullWidth
            autoFocus
            name="firstname"
            value={form.firstname}
            onChange={handleChange}
            error={Boolean(fieldErrors.firstname)}
            helperText={fieldErrors.firstname}
            sx={inputBorderSx}
          />

          <TextField
            required
            id="lastname"
            label={t('LABEL_LASTNAME')}
            variant="outlined"
            placeholder={t('PLACEHOLDER_LASTNAME')}
            type="text"
            fullWidth
            name="lastname"
            value={form.lastname}
            onChange={handleChange}
            error={Boolean(fieldErrors.lastname)}
            helperText={fieldErrors.lastname}
            sx={inputBorderSx}
          />
        </Stack>

        <Stack gap={1.5}>
          <TextField
            required
            id="email"
            label={t('LABEL_EMAIL')}
            variant="outlined"
            placeholder={t('PLACEHOLDER_EMAIL')}
            type="email"
            fullWidth
            name="email"
            value={form.email}
            onChange={handleChange}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
            sx={inputBorderSx}
          />

          <TextField
            required
            id="password"
            label={t('LABEL_PASSWORD')}
            variant="outlined"
            placeholder={t('PLACEHOLDER_PASSWORD')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            name="password"
            value={form.password}
            onChange={handleChange}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputBorderSx}
          />

          <PasswordStrengthIndicator
            password={form.password}
            visible={passwordFocused}
            labels={{
              uppercase: t('AT_LEAST_ONE_UPPERCASE_LETTER'),
              lowercase: t('AT_LEAST_ONE_LOWERCASE_LETTER'),
              length: t('AT_LEAST_HEIGHT_CHARACTERS'),
            }}
          />

           <MuiTelInput
            required
            id="phone"
            name="phoneNumber"
            label={t('LABEL_PHONE')}
            defaultCountry={
            (defaultCountry && getCode(defaultCountry)) || (countryName && getCode(countryName)) || 'FR'
            }
            forceCallingCode
            value={form.phoneNumber}
            onChange={handlePhoneChange}
            error={Boolean(fieldErrors.phoneNumber)}
            helperText={fieldErrors.phoneNumber || ''}
            fullWidth
            sx={inputBorderSx}
          />

          <Stack css={namesContainerStyles} gap={1.5}>
            <TextField
              required
              id="street"
              label={t('LABEL_STREET')}
              variant="outlined"
              placeholder={t('PLACEHOLDER_STREET')}
              type="text"
              fullWidth
              name="street"
              value={form.street}
              onChange={handleChange}
              error={Boolean(fieldErrors.street)}
              helperText={fieldErrors.street}
              sx={inputBorderSx}
            />

            <TextField
              required
              id="zipCode"
              label={t('LABEL_ZIP_CODE')}
              variant="outlined"
              placeholder={t('PLACEHOLDER_ZIP_CODE')}
              type="text"
              fullWidth
              name="zipCode"
              value={form.zipCode}
              onChange={handleChange}
              error={Boolean(fieldErrors.zipCode)}
              helperText={fieldErrors.zipCode}
              sx={inputBorderSx}
            />
          </Stack>

          <Stack css={namesContainerStyles} gap={1.5}>
            <TextField
              required
              id="city"
              label={t('LABEL_CITY')}
              variant="outlined"
              placeholder={t('PLACEHOLDER_CITY')}
              type="text"
              fullWidth
              name="city"
              value={form.city}
              onChange={handleChange}
              error={Boolean(fieldErrors.city)}
              helperText={fieldErrors.city}
              sx={inputBorderSx}
            />

            <FormControl fullWidth>
              <Autocomplete
                options={countriesList}
                getOptionLabel={(option) => option.commonName}
                value={
                  countriesList.find(
                    (item) => item.commonName === form.country,
                  ) || null
                }
                onChange={(event, newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    country: newValue ? newValue.commonName : '',
                  }));
                  setFieldErrors((prev) => ({ ...prev, country: '' }));
                }}
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option.countryCode}
                    value={option.countryCode}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        component="img"
                        src={`https://flagcdn.com/w40/${option.countryCode.toLowerCase()}.png`}
                        alt={`Flag of ${option.commonName}`}
                        sx={{ width: 24, height: 16, borderRadius: '2px' }}
                      />
                      {option.commonName}
                    </Box>
                  </MenuItem>
                )}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    label={t('LABEL_COUNTRY')}
                    variant="outlined"
                    fullWidth
                    error={Boolean(fieldErrors.country)}
                    helperText={fieldErrors.country}
                    sx={inputBorderSx}
                  />
                )}
              />
            </FormControl>
          </Stack>
        </Stack>

        {/* Checkboxes (T&C, newsletter) */}
        <Stack mt={1.5} mb={4}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptTnc}
                  sx={{ color: registerColors.checkboxColor }}
                  onChange={(e) => {
                    setAcceptTnc(e.target.checked);
                    setFieldErrors((prev) => ({ ...prev, acceptTnc: '' }));
                  }}
                />
              }
              label={
                <Typography variant="body1" component="span">
                  {t('LABEL_I_ACCEPT')}{' '}
                  <Typography
                    variant="body1"
                    component={Link}
                    to="https://raisemyfunds.co/wp-content/uploads/2024/08/TCs-RaiseMyFunds.pdf"
                    sx={{
                      textDecoration: 'underline',
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                    }}
                  >
                    {t('LABEL_TERMS_AND_CONDITIONS')}
                  </Typography>
                  <Typography variant="body1" component="span">*</Typography>
                </Typography>
              }
            />
            {fieldErrors.acceptTnc && (
              <Typography color="error" variant="body2">
                {fieldErrors.acceptTnc}
              </Typography>
            )}
          </FormGroup>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newsletter}
                  sx={{ color: registerColors.checkboxColor }}
                  onChange={(e) => {
                    setNewsletter(e.target.checked);
                    setFieldErrors((prev) => ({ ...prev, newsletter: '' }));
                  }}
                />
              }
              label={`${t('LABEL_NEWSLETTER')}*`}
            />
            {fieldErrors.newsletter && (
              <Typography color="error" variant="body2">
                {fieldErrors.newsletter}
              </Typography>
            )}
          </FormGroup>
        </Stack>

        {error && (
          <Typography color="error" mb={2}>
            {error}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSubmitting || !acceptTnc || !newsletter}
        >
          {isSubmitting ? <CircularProgress /> : t('BUTTON_CONTINUE')}
        </Button>

        <Typography
          mt={6}
          variant="body1"
          textAlign={'center'}
          sx={{ color: registerColors.textLink }}
        >
          {t('TEXT_ALREADY_HAVE_AN_ACCOUNT')}{' '}
          <Typography
            variant="body1"
            component={Link}
            to={'/login'}
            sx={{ textDecoration: 'underline', color: registerColors.textLink }}
          >
            {t('LINK_SIGN_IN')}
          </Typography>
        </Typography>
      </form>
    </Stack>
  );
};

export default RegisterContent;
